<template>
  <div class="page-wrapper">
    <div class="container-fluid px-0 py-0">
      <div class="main-card shadow-sm">
        <div
          class="card-header-custom border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3"
        >
          <div class="d-flex align-items-center gap-3">
            <h1 class="h4 mb-0 fw-bold text-dark">Regionales</h1>
            <span class="badge bg-light text-dark border shadow-sm">
              <i class="fas fa-globe-americas text-primary me-1"></i>
              Total: {{ regionales.length }}
            </span>
          </div>

          <div>
            <button
              @click="openModal(null)"
              class="btn btn-primary btn-sm shadow-sm d-flex align-items-center hover-elevate"
            >
              <i class="fas fa-plus-circle me-2"></i> Nueva Regional
            </button>
          </div>
        </div>

        <div class="p-4 bg-light border-bottom">
          <div class="row">
            <div class="col-md-5">
              <div class="input-group">
                <span class="input-group-text bg-white border-end-0 text-muted">
                  <i class="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  v-model="searchTerm"
                  class="form-control border-start-0 ps-0"
                  placeholder="Buscar por nombre, código o provincia..."
                />
              </div>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div
            v-if="!loading && regionales.length === 0"
            class="text-center py-5"
          >
            <div class="text-secondary mb-3">
              <i class="fas fa-globe-americas fa-3x opacity-50"></i>
            </div>
            <p class="text-muted">No se encontraron regionales.</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead class="bg-light text-secondary text-uppercase small">
                <tr>
                  <th class="ps-4 py-1 border-0">Nombre</th>
                  <th class="py-1 border-0">Código</th>
                  <th class="py-1 border-0">Provincia</th>
                  <th class="py-1 border-0 text-center">Estado</th>
                  <th class="pe-4 py-1 border-0 text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <TableSpinner
                  v-if="loading"
                  :colspan="5"
                  text="Cargando regionales..."
                />

                <template v-else>
                  <tr v-for="regional in regionales" :key="regional.id">
                    <td class="ps-4 py-1 fw-bold text-dark">
                      {{ regional.name }}
                    </td>

                    <td class="py-1 text-monospace text-muted">
                      {{ regional.code }}
                    </td>

                    <td class="py-1 text-dark">
                      <i
                        class="fas fa-map-marker-alt text-secondary me-1 small"
                      ></i>
                      {{ regional.province_name }}
                    </td>

                    <td class="py-1 text-center">
                      <span
                        class="badge rounded-pill px-3"
                        :class="
                          regional.status === 1
                            ? 'bg-success-subtle text-success border border-success-subtle'
                            : 'bg-danger-subtle text-danger border border-danger-subtle'
                        "
                      >
                        {{ regional.status === 1 ? "Activo" : "Inactivo" }}
                      </span>
                    </td>

                    <td class="pe-4 py-1 text-end">
                      <ActionDropdown>
                        <button
                          class="dropdown-item d-flex align-items-center"
                          @click="editRegional(regional)"
                        >
                          <i class="fas fa-edit text-primary fa-fw me-2"></i>
                          Editar
                        </button>
                        <div class="dropdown-divider"></div>
                        <button
                          class="dropdown-item d-flex align-items-center text-danger"
                          @click="deleteRegionalById(regional.id)"
                        >
                          <i class="fas fa-trash-alt fa-fw me-2"></i> Eliminar
                        </button>
                      </ActionDropdown>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <div
          class="card-footer-custom p-3 bg-light border-top d-flex justify-content-end"
          v-if="lastPage > 1"
        >
          <div class="btn-group shadow-sm">
            <button
              @click="fetchRegionales(1)"
              class="btn btn-white border"
              :disabled="currentPage === 1"
            >
              Primera
            </button>
            <button
              @click="fetchRegionales(currentPage - 1)"
              class="btn btn-white border"
              :disabled="currentPage === 1"
            >
              Anterior
            </button>
            <button
              @click="fetchRegionales(currentPage + 1)"
              class="btn btn-white border"
              :disabled="!hasMorePages"
            >
              Siguiente
            </button>
            <button
              @click="fetchRegionales(lastPage)"
              class="btn btn-white border"
              :disabled="currentPage === lastPage"
            >
              Última
            </button>
          </div>
        </div>
      </div>
    </div>

    <ModalFormRegional
      :isOpen="modalOpen"
      :regional="selectedRegional"
      :provinces="provinces"
      @close="modalOpen = false"
      @success="handleModalSuccess"
    />
  </div>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import { debounce } from "lodash";
import {
  fetchRegionals,
  deleteRegional,
} from "@/services/api/regionalService.js";
import { fetchProvinces } from "@/services/api/provinceService.js";
import ModalFormRegional from "@/components/regional/ModalFormRegional.vue";
import userConfirmation from "@/utils/userConfirmation.js";

import TableSpinner from "@/components/ui/TableSpinner.vue";
import ActionDropdown from "@/components/common/ActionDropdown.vue";

export default {
  components: {
    ModalFormRegional,
    TableSpinner,
    ActionDropdown,
  },
  setup() {
    const regionales = ref([]);
    const provinces = ref([]);
    const loading = ref(false);
    const searchTerm = ref("");
    const orderByMethod = ref("asc");
    const perPage = ref(10);
    const status = ref(null);

    const modalOpen = ref(false);
    const selectedRegional = ref(null);

    const currentPage = ref(1);
    const lastPage = ref(1);
    const hasMorePages = ref(false);

    const fetchRegionales = async (page = 1) => {
      loading.value = true;
      try {
        const data = await fetchRegionals({
          search: searchTerm.value,
          orderByMethod: orderByMethod.value,
          perPage: perPage.value,
          status: status.value,
          page,
        });
        regionales.value = data.data || [];
        currentPage.value = data.meta.current_page;
        lastPage.value = data.meta.last_page;
        hasMorePages.value = data.meta.current_page < data.meta.last_page;
      } catch (error) {
        console.error("Error fetching regionales:", error);
      } finally {
        loading.value = false;
      }
    };

    const debouncedFetch = debounce(() => {
      fetchRegionales(1);
    }, 500);

    watch(searchTerm, () => {
      debouncedFetch();
    });

    watch([orderByMethod, perPage, status], () => {
      fetchRegionales(1);
    });

    const fetchProvinciasList = async () => {
      try {
        const response = await fetchProvinces({
          perPage: 100,
          orderByMethod: orderByMethod.value,
          status: status.value,
        });
        provinces.value = response.data || [];
      } catch (error) {
        console.error("Error fetching provinces:", error);
      }
    };

    const openModal = (regional) => {
      selectedRegional.value = regional;
      modalOpen.value = true;
    };

    const handleModalSuccess = () => {
      modalOpen.value = false;
      fetchRegionales(currentPage.value);
    };

    const editRegional = (regional) => {
      openModal(regional);
    };

    const deleteRegionalById = async (id) => {
      const confirmed = await userConfirmation(
        `¿Está seguro de eliminar la regional seleccionada?`,
      );
      if (!confirmed) return;
      try {
        await deleteRegional(id);
        fetchRegionales(currentPage.value);
      } catch (error) {
        console.error("Error deleting regional:", error);
      }
    };

    onMounted(() => {
      fetchProvinciasList();
      fetchRegionales();
    });

    return {
      provinces,
      regionales,
      loading,
      searchTerm,
      orderByMethod,
      perPage,
      status,
      fetchRegionales,
      modalOpen,
      selectedRegional,
      openModal,
      handleModalSuccess,
      editRegional,
      deleteRegionalById,
      currentPage,
      lastPage,
      hasMorePages,
    };
  },
};
</script>

<style scoped>
.page-wrapper {
  background-color: #f8f9fa;
  min-height: 100vh;
}

.main-card {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.card-header-custom {
  padding: 1.5rem 2rem;
  background-color: white;
}

.card-footer-custom {
  background-color: #f8f9fa;
}

.input-group-text {
  background-color: white;
  border-color: #ced4da;
}

.form-control {
  border-color: #ced4da;
  padding: 0.6rem 0.75rem;
  font-size: 0.95rem;
}

.form-control:focus {
  box-shadow: none;
  border-color: #86b7fe;
  background-color: #fbfdff;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

.text-monospace {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
}

.bg-primary-subtle {
  background-color: #cfe2ff !important;
  color: #084298 !important;
}

.fa-fw {
  width: 1.25em;
  text-align: center;
}

.btn-white {
  background-color: white;
  color: #495057;
}

.btn-white:hover {
  background-color: #e9ecef;
}

.hover-elevate:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  transition: all 0.2s;
}

@media (max-width: 768px) {
  .card-header-custom {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
